import React, { useState, useEffect, useMemo } from 'react';
import { FileCheck, FileText, Award, Eye, Download, Printer, Loader2, FolderOpen, ScrollText, ShieldCheck, Search, FileArchive, Inbox, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import DocPreviewLightbox from './DocPreviewLightbox';

const API = process.env.REACT_APP_BACKEND_URL;

// V36.95 — Config par type avec libellé court (badge) et groupe (tab)
const DOC_TYPE_CONFIG = {
  attestation_signee: {
    label: 'Attestation signée', group: 'attestations', icon: FileCheck,
    bg: 'bg-blue-50', color: 'text-blue-600',
    badge: 'bg-blue-700 text-white border-blue-800 shadow-sm',
    accent: 'border-l-blue-600',
  },
  conditions_generales_particulieres_signees: {
    label: 'CG/CP signées', group: 'attestations', icon: FileText,
    bg: 'bg-emerald-50', color: 'text-emerald-600',
    badge: 'bg-emerald-700 text-white border-emerald-800 shadow-sm',
    accent: 'border-l-emerald-600',
  },
  attestation_ftpe_signee: {
    label: 'Attestation FTPE', group: 'ftpe', icon: Award,
    bg: 'bg-amber-50', color: 'text-amber-600',
    badge: 'bg-amber-600 text-white border-amber-700 shadow-sm',
    accent: 'border-l-amber-500',
  },
  attestation_ftpe_generee: {
    label: 'Attestation FTPE', group: 'ftpe', icon: Award,
    bg: 'bg-amber-50', color: 'text-amber-600',
    badge: 'bg-amber-600 text-white border-amber-700 shadow-sm',
    accent: 'border-l-amber-500',
  },
  devis_ess: {
    label: 'Devis ESS', group: 'propositions', icon: ScrollText,
    bg: 'bg-violet-50', color: 'text-violet-600',
    badge: 'bg-violet-700 text-white border-violet-800 shadow-sm',
    accent: 'border-l-violet-600',
  },
  devis_ess_genere: {
    label: 'Devis ESS', group: 'propositions', icon: ScrollText,
    bg: 'bg-violet-50', color: 'text-violet-600',
    badge: 'bg-violet-700 text-white border-violet-800 shadow-sm',
    accent: 'border-l-violet-600',
  },
  certificat_adhesion: {
    label: "Certificat O'SCOP", group: 'certificats', icon: ShieldCheck,
    bg: 'bg-indigo-50', color: 'text-indigo-600',
    badge: 'bg-indigo-700 text-white border-indigo-800 shadow-sm',
    accent: 'border-l-indigo-600',
  },
};

const DEFAULT_CONFIG = DOC_TYPE_CONFIG.attestation_signee;

const GROUPS = [
  { key: 'all', label: 'Tous', icon: FolderOpen },
  { key: 'propositions', label: 'Propositions / Devis ESS', icon: ScrollText },
  { key: 'attestations', label: 'Attestations RCDS', icon: FileCheck },
  { key: 'ftpe', label: 'FTPE', icon: Award },
  { key: 'certificats', label: 'Certificats', icon: ShieldCheck },
];

const formatDate = (d) => d
  ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
  : '-';

const getGroup = (typeDoc) =>
  (DOC_TYPE_CONFIG[typeDoc]?.group) || 'autres';

export default function MemberDocsInline({ membreId, token }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewState, setPreviewState] = useState({ open: false, index: 0 });
  const [actionState, setActionState] = useState({ docId: null, mode: null });
  const [activeGroup, setActiveGroup] = useState('all');
  const [search, setSearch] = useState('');
  const [zipping, setZipping] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!membreId || !token) {
      setLoading(false);
      return undefined;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/membres/${membreId}/documents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setDocs(data.documents || []);
        }
      } catch {
        // silent — l'utilisateur verra le skeleton se vider
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [membreId, token]);

  // V36.95 — Filtrage et regroupement
  const groupCounts = useMemo(() => {
    const out = { all: docs.length };
    docs.forEach((d) => {
      const g = getGroup(d.type_document);
      out[g] = (out[g] || 0) + 1;
    });
    return out;
  }, [docs]);

  const filteredDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (activeGroup !== 'all') {
        if (getGroup(d.type_document) !== activeGroup) return false;
      }
      if (!q) return true;
      const hay = `${d.original_filename || ''} ${d.type_document || ''} ${d.description || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [docs, activeGroup, search]);

  const handleAction = async (doc, mode) => {
    if (mode === 'view') {
      const idx = filteredDocs.findIndex((d) => d.id === doc.id);
      setPreviewState({ open: true, index: idx >= 0 ? idx : 0 });
      return;
    }
    setActionState({ docId: doc.id, mode });
    try {
      const res = await fetch(`${API}/api/admin/membres/${membreId}/documents/${doc.id}/download?mode=view`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          if (body?.detail) detail = body.detail;
        } catch { /* noop */ }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      if (mode === 'download') {
        const a = document.createElement('a');
        a.href = url; a.download = doc.original_filename || 'document';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Téléchargement lancé');
      } else if (mode === 'print') {
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;top:-10000px;left:-10000px;width:1px;height:1px;opacity:0;';
        document.body.appendChild(iframe);
        iframe.onload = () => {
          try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
          catch { window.open(url, '_blank'); }
          setTimeout(() => { document.body.removeChild(iframe); URL.revokeObjectURL(url); }, 1000);
        };
        iframe.src = url;
        toast.success('Aperçu impression ouvert');
      }
    } catch (err) {
      const msg = err?.message || "Erreur lors de l'action";
      if (msg.includes('introuvable') || msg.includes('404')) {
        toast.error('Fichier introuvable sur le serveur', {
          description: 'Le PDF a été perdu (container PROD redéployé). Demandez à un Super Admin de cliquer sur « Régénérer PDF » dans la proposition source pour le reconstituer à l\'identique.',
          duration: 9000,
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setActionState({ docId: null, mode: null });
    }
  };

  // V36.95 — Export ZIP groupé
  const handleExportZip = async () => {
    setZipping(true);
    try {
      const res = await fetch(`${API}/api/admin/membres/${membreId}/documents/export-zip`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const b = await res.json(); if (b?.detail) detail = b.detail; } catch { /* noop */ }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `documents_membre_${membreId.slice(0, 8)}.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`ZIP téléchargé (${docs.length} documents)`);
    } catch (err) {
      toast.error(err?.message || 'Export ZIP impossible');
    } finally {
      setZipping(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-4 pt-4 border-t border-dashed border-gray-200" data-testid={`docs-inline-${membreId}-skeleton`}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-200 to-purple-200 animate-pulse" />
          <div className="h-3 w-40 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white border border-gray-100 animate-pulse">
              <div className="w-9 h-9 rounded-lg bg-gray-100 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-gray-100 rounded w-3/4" />
                <div className="h-2 bg-gray-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (docs.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-dashed border-gray-200" data-testid={`docs-inline-${membreId}`}>
      {/* === Header avec titre, compteur, export ZIP === */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm">
            <FolderOpen className="h-3.5 w-3.5 text-white" />
          </div>
          Documents &amp; Certificats
          <Badge variant="outline" className="text-[10px] px-2 py-0.5 bg-indigo-600 text-white border-indigo-700 shadow-sm font-semibold">
            {docs.length}
          </Badge>
        </h4>
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-[11px] px-2.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50 active:scale-[0.97] transition-all"
            onClick={handleExportZip}
            disabled={zipping || docs.length === 0}
            data-testid={`docs-inline-zip-${membreId}`}
            title="Télécharger tous les documents en un fichier ZIP"
          >
            {zipping ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <FileArchive className="h-3 w-3 mr-1.5" />}
            ZIP
          </Button>
        </div>
      </div>

      {/* === Barre de filtres : tabs par groupe + recherche === */}
      <div className="mb-3 space-y-2">
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid={`docs-inline-tabs-${membreId}`}>
          {GROUPS.map((g) => {
            const count = groupCounts[g.key] || 0;
            if (g.key !== 'all' && count === 0) return null;
            const active = activeGroup === g.key;
            const G = g.icon;
            return (
              <button
                key={g.key}
                type="button"
                onClick={() => setActiveGroup(g.key)}
                className={cn(
                  'shrink-0 inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full border text-[11px] font-semibold transition-all',
                  active
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-indigo-700 shadow-sm'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-700'
                )}
                data-testid={`docs-inline-tab-${g.key}`}
              >
                <G className="h-3 w-3" />
                {g.label}
                <span className={cn(
                  'ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full text-[9px] font-bold',
                  active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                )}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un document (nom, type, description)…"
            className="w-full h-8 pl-8 pr-8 text-xs rounded-lg border border-gray-200 bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
            data-testid={`docs-inline-search-${membreId}`}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              data-testid={`docs-inline-search-clear-${membreId}`}
              title="Effacer la recherche"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* === Liste filtrée === */}
      {filteredDocs.length === 0 ? (
        <div className="py-8 px-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-center" data-testid={`docs-inline-empty-${membreId}`}>
          <Inbox className="h-7 w-7 text-gray-300 mx-auto mb-2" />
          <p className="text-xs font-medium text-gray-500">
            {search ? 'Aucun document ne correspond à la recherche' : 'Aucun document dans cette catégorie'}
          </p>
          {(search || activeGroup !== 'all') && (
            <button
              type="button"
              onClick={() => { setSearch(''); setActiveGroup('all'); }}
              className="mt-2 text-[11px] text-indigo-600 font-semibold hover:underline"
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2" data-testid={`docs-inline-list-${membreId}`}>
          {filteredDocs.map(doc => {
            const conf = DOC_TYPE_CONFIG[doc.type_document] || DEFAULT_CONFIG;
            const Icon = conf.icon;
            const isActing = actionState.docId === doc.id;
            return (
              <div
                key={doc.id}
                className={cn(
                  "group relative flex items-start gap-3 p-3 rounded-lg bg-white border border-gray-200 border-l-4 hover:shadow-md hover:border-gray-300 transition-all duration-200",
                  conf.accent
                )}
                data-testid={`inline-doc-${doc.id}`}
              >
                {/* Icône avec halo coloré */}
                <div className={cn(
                  "w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ring-1 ring-gray-100 transition-transform group-hover:scale-105",
                  conf.bg
                )}>
                  <Icon className={cn("h-4 w-4", conf.color)} />
                </div>

                {/* Contenu : nom + badges */}
                <div className="flex-1 min-w-0 space-y-1.5">
                  <p
                    className="text-sm font-medium text-gray-900 leading-snug break-words"
                    title={doc.original_filename}
                    data-testid={`inline-doc-filename-${doc.id}`}
                  >
                    {doc.original_filename || 'Document sans nom'}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 h-5 font-semibold", conf.badge)}>
                      {conf.label}
                    </Badge>
                    {doc.source === 'ged_genere' && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-slate-700 text-white border-slate-800 shadow-sm font-semibold">
                        Auto
                      </Badge>
                    )}
                    <span className="text-[10px] text-gray-500 font-medium ml-auto sm:ml-0">
                      {formatDate(doc.created_at)}
                    </span>
                  </div>
                </div>

                {/* Actions : Voir / Télécharger / Imprimer */}
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-transparent hover:border-blue-200 active:scale-95 transition-all"
                    onClick={() => handleAction(doc, 'view')}
                    title="Aperçu rapide"
                    data-testid={`inline-view-${doc.id}`}
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border border-transparent hover:border-emerald-200 active:scale-95 transition-all disabled:opacity-50"
                    onClick={() => handleAction(doc, 'download')}
                    disabled={isActing && actionState.mode === 'download'}
                    title="Télécharger"
                    data-testid={`inline-dl-${doc.id}`}
                  >
                    {isActing && actionState.mode === 'download'
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Download className="h-3.5 w-3.5" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-amber-600 hover:text-amber-700 hover:bg-amber-50 border border-transparent hover:border-amber-200 active:scale-95 transition-all disabled:opacity-50"
                    onClick={() => handleAction(doc, 'print')}
                    disabled={isActing && actionState.mode === 'print'}
                    title="Imprimer"
                    data-testid={`inline-print-${doc.id}`}
                  >
                    {isActing && actionState.mode === 'print'
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Printer className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox de prévisualisation rapide avec navigation ← → */}
      <DocPreviewLightbox
        open={previewState.open}
        onClose={() => setPreviewState((p) => ({ ...p, open: false }))}
        documents={filteredDocs}
        initialIndex={previewState.index}
        membreId={membreId}
        token={token}
        typeConfig={DOC_TYPE_CONFIG}
      />
    </div>
  );
}
